import { css } from 'aphrodite';
import * as React from 'react';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import type { ISupportTicket } from '../../../models/Support/ISupportTicket';
import { ThreadStatus, ThreadType } from '../../../models/Support/SupportEnums';
import { useAdminAccountQuery } from '../../../queries/Admin/Account/useAdminAccountQuery';
import { useAdminAccountUsersQuery } from '../../../queries/AdminTools/useAdminAccountUsersQuery';
import {
	toAgentTicketCountMap,
	useSupportAgentTicketCountsQuery,
} from '../../../queries/Support/useSupportAgentTicketCounts';
import { useSupportCategoriesQuery } from '../../../queries/Support/useSupportCategories';
import {
	sortSupportAgentsByName,
	useSupportTeamPresentation,
	useSupportTeamQuery,
} from '../../../queries/Support/useSupportTeam';
import { useSupportThreadsQuery } from '../../../queries/Support/useSupportThreadsQuery';
import { useSupportTicketQuery } from '../../../queries/Support/useSupportTicketQuery';
import { useSupportTicketsQuery } from '../../../queries/Support/useSupportTicketsQuery';
import {
	getInitials,
	getSupportContactProfilePicSrc,
	getUserReferenceName,
} from '../../../utils/Support/supportHelpers';
import { Avatar } from '@HoudiniUI/Avatar';
import { MessageCircleIcon } from '../../../web/components/svgs/icons/MessageCircleIcon';
import { Button } from '../../../web/components-v2/Button';
import { Pill } from '../../../web/components-v2/Pill';
import { HealthScoreCircle } from '../../components/accountDetails/HealthScoreCircle';
import { isVipAccount, VipBadge } from '../../components/accountDetails/VipBadge';
import { ScheduleMeetingModal } from '../../components/ScheduleMeetingModal';
import { CreateGitHubIssueModal, LinkGitHubIssueModal } from './GitHubIssueModals';
import { GitHubPanel } from './GitHubPanel';
import { DetailRow as Row } from './DetailRow';
import { PreviousConversationsModal } from './PreviousConversationsModal';
import { inboxStyles as s } from './styles/inboxStyles';
import {
	formatDate,
	getAccountStatusInfo,
	getAccountWebsiteUrl,
	getDudaEditorUrl,
	getShareFileFolderUrl,
	getTicketContactLabel,
	getWebsiteProviderInfo,
} from './supportTicketPresentation';
import { TicketAdsPanel } from './TicketAdsPanel';
import { TicketDetailsEditor } from './TicketDetailsEditor';
import type { TicketFieldShortcut } from './useTicketShortcuts';
import { TicketPreviewModal } from './TicketPreviewModal';

interface TicketDetailsBodyProps {
	ticketId: string;
	/** Navigate the inbox to another ticket from the previous-conversations preview modal. */
	onSelectTicket?: (ticketId: string) => void;
	/** Opens the customer picker for customerless tickets. */
	onChangeCustomer?: () => void;
	/** Called after the ticket leaves the agent's plate (closed, spam, archived, or assigned). */
	onTicketLeftView?: () => void;
	/**
	 * False while this body stays mounted but hidden (the desktop drawer keeps it mounted behind the
	 * Assistant panel). The modals below render as fixed overlays, so they must close with it.
	 */
	isVisible?: boolean;
	/** Which editor field the `a` / `s` shortcuts have raised; owned by the panel that hosts them. */
	shortcutField?: TicketFieldShortcut | null;
	onShortcutFieldChange?: (field: TicketFieldShortcut | null) => void;
}

const PREVIOUS_CONVERSATION_LIMIT = 6;

/** Tracks where the preview was opened from, so only the list route offers a way back. */
interface TicketPreviewTarget {
	ticketId: string;
	fromConversationList: boolean;
}

type PreviousConversationTicket = Pick<ISupportTicket, 'id' | 'number' | 'subject'>;

const PreviousConversations: React.FC<{
	tickets: PreviousConversationTicket[];
	onPreviewTicket: (ticketId: string) => void;
	onViewAll: () => void;
}> = ({ tickets, onPreviewTicket, onViewAll }) => {
	if (tickets.length === 0) return null;

	const visibleTickets = tickets.slice(0, PREVIOUS_CONVERSATION_LIMIT);
	const hasOverflow = tickets.length > PREVIOUS_CONVERSATION_LIMIT;

	return (
		<>
			<div className={css(s.detailSectionTitle)}>Conversations ({tickets.length})</div>
			<div className={css(s.linkedList)}>
				{visibleTickets.map(ticket => (
					<Button
						key={ticket.id}
						variant='ghost'
						size='sm'
						styles={[s.linkedRow]}
						onClick={() => onPreviewTicket(ticket.id)}
						title={`#${ticket.number} · ${ticket.subject}`}
					>
						<span className={css(s.linkedIcon)}>
							<MessageCircleIcon width={14} height={14} />
						</span>
						<span className={css(s.linkedText)}>{ticket.subject}</span>
					</Button>
				))}
				{hasOverflow && (
					<Button variant='ghost' size='sm' styles={[s.linkedRow]} onClick={onViewAll}>
						<span className={css(s.linkedIcon)}>&#8943;</span>
						<span className={css(s.linkedText, s.linkedViewAll)}>View all {tickets.length}</span>
					</Button>
				)}
			</div>
		</>
	);
};

const CONTACT_USER_PAGE_SIZE = 10;

/**
 * The ticket details content itself, without any panel chrome. Shared by the desktop drawer
 * ({@link RightPanel}) and the phone details sheet ({@link PhoneTicketStage}) so the two surfaces
 * cannot drift apart.
 */
export const TicketDetailsBody: React.FC<TicketDetailsBodyProps> = ({
	ticketId,
	onSelectTicket,
	onChangeCustomer,
	onTicketLeftView,
	isVisible = true,
	shortcutField,
	onShortcutFieldChange,
}) => {
	const userSession = useAdminUserSession();
	const [gitHubModal, setGitHubModal] = React.useState<'create' | 'link' | null>(null);
	const [preview, setPreview] = React.useState<TicketPreviewTarget | null>(null);
	const [showAllConversations, setShowAllConversations] = React.useState(false);
	const [conversationsPage, setConversationsPage] = React.useState(1);
	const [showScheduleMeetingModal, setShowScheduleMeetingModal] = React.useState(false);

	// Close transient modals when switching tickets, or when this body is hidden behind another panel.
	React.useEffect(() => {
		setGitHubModal(null);
		setPreview(null);
		setShowAllConversations(false);
		setConversationsPage(1);
		setShowScheduleMeetingModal(false);
	}, [ticketId, isVisible]);

	const { data: ticket } = useSupportTicketQuery(ticketId, { refetchInterval: false });
	const { data: account } = useAdminAccountQuery({
		accountId: ticket?.accountId ?? '',
		enabled: !!ticket?.accountId,
		meta: { toastErrorMessage: 'Failed to load account details' },
	});
	// The zone lives on the customer's Levitate user. Searching the account by email narrows the page; the
	// contactId match picks the right user, since the search is a substring match and the query keeps the
	// previous ticket's rows as a placeholder. The endpoint lists active users first.
	const canLoadContactUser = !!ticket?.accountId && !!ticket.contactId && !!ticket.contactEmail?.trim();
	const { data: contactUsers } = useAdminAccountUsersQuery({
		accountId: canLoadContactUser ? ticket.accountId : '',
		page: 0,
		pageSize: CONTACT_USER_PAGE_SIZE,
		statuses: [],
		roles: [],
		search: ticket?.contactEmail?.trim() ?? '',
	});
	const contactUser = canLoadContactUser
		? contactUsers?.values.find(user => user.accountId === ticket.accountId && user.contactId === ticket.contactId)
		: undefined;
	const { data: threads } = useSupportThreadsQuery(ticketId, { refetchInterval: false });
	const categoriesQuery = useSupportCategoriesQuery(ticket?.mailboxId || '');
	const categories = categoriesQuery.data?.values ?? [];
	const { data: teamData } = useSupportTeamQuery(ticket?.mailboxId || '');
	const { agentNameMap } = useSupportTeamPresentation(ticket?.mailboxId || '');
	const teamMembers = React.useMemo(
		() => sortSupportAgentsByName(teamData?.values ?? []).map(a => ({ id: a.agentId, name: a.agentName || a.agentId })),
		[teamData]
	);
	// Keyed on the ticket's own desk, not the one selected in the inbox: the assignee list comes from
	// that desk's roster, so its counts are the only ones the picker can be read against.
	const { data: agentTicketCountsData } = useSupportAgentTicketCountsQuery(ticket?.mailboxId || '');
	const agentTicketCounts = React.useMemo(() => toAgentTicketCountMap(agentTicketCountsData), [agentTicketCountsData]);

	const linkedReady = !!ticket?.mailboxId && !!ticket?.contactEmail;
	const linkedQuery = useSupportTicketsQuery({
		mailboxId: linkedReady ? ticket.mailboxId : '',
		contactEmail: ticket?.contactEmail,
		includeInactive: true,
		page: 1,
		pageSize: 100,
		refetchInterval: false,
	});
	const linkedTicketValues = linkedQuery.data?.values;
	const linkedTickets = React.useMemo(
		() =>
			(linkedTicketValues ?? [])
				.filter(linkedTicket => linkedTicket.id !== ticketId)
				.sort((a, b) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime()),
		[linkedTicketValues, ticketId]
	);

	if (!ticket) return <div className={css(s.empty)}>Select a ticket to see details.</div>;

	const messageCount = threads?.values?.filter(t => t.type !== ThreadType.Note && t.type !== ThreadType.AiDraft).length;
	const noteCount = threads?.values?.filter(t => t.type === ThreadType.Note).length;
	const hasPendingAiDraft = !!threads?.values?.some(
		t => t.type === ThreadType.AiDraft && t.status === ThreadStatus.Draft
	);
	const contactLabel = getTicketContactLabel(ticket);
	const hasCustomer = !!ticket.contactEmail?.trim();
	const contactTimeZone = contactUser?.userPreferences?.timeZone?.trim();
	const accountStatus = getAccountStatusInfo(account);
	const isVip = isVipAccount(account);
	const websiteProvider = getWebsiteProviderInfo(account);
	const accountLinks = [
		{ href: getAccountWebsiteUrl(account), label: 'Website' },
		{ href: getDudaEditorUrl(account), label: 'Duda editor' },
		{ href: getShareFileFolderUrl(account), label: 'ShareFile folder' },
		{ label: 'Schedule Meeting', onClick: account ? () => setShowScheduleMeetingModal(true) : undefined },
	].filter(link => !!link.href || !!link.onClick);

	return (
		<>
			<div className={css(s.detailHeader)}>
				<Avatar
					imgSrc={getSupportContactProfilePicSrc(ticket)}
					altText={contactLabel === '—' ? 'Contact' : contactLabel}
					fallbackText={getInitials(ticket.contactName, ticket.contactEmail)}
					styles={[s.detailAvatar]}
				/>
				<div className={css(s.detailIdentity)}>
					<div className={css(s.detailName)}>{ticket.contactName || (hasCustomer ? '—' : 'No customer assigned')}</div>
					{hasCustomer ? (
						<div className={css(s.detailEmail)}>{ticket.contactEmail}</div>
					) : (
						onChangeCustomer && (
							<Button type='button' variant='link' styles={[s.detailCustomerAction]} onClick={onChangeCustomer}>
								Add customer
							</Button>
						)
					)}
				</div>
				{account && <HealthScoreCircle health={account.additionalInfo?.health} styles={[s.detailHealthScore]} />}
			</div>

			<div className={css(s.detailSectionTitle)}>Account</div>
			<div className={css(s.detailRows)}>
				{ticket.accountName && (
					<Row label='Company'>
						<span className={css(s.detailCompany)}>
							<a
								href={`/admin.html#/accounts/${ticket.accountId}`}
								target='_blank'
								rel='noopener noreferrer'
								className={css(s.detailLink)}
							>
								{ticket.accountName}
							</a>
							{isVip && <VipBadge />}
						</span>
					</Row>
				)}
				{ticket.csm && <Row label='CSM'>{getUserReferenceName(ticket.csm)}</Row>}
				{ticket.industry && <Row label='Industry'>{ticket.industry}</Row>}
				{ticket.systemOfRecord && <Row label='System of record'>{ticket.systemOfRecord}</Row>}
				{!!ticket.enabledIntegrations?.length && (
					<Row label='Integrations'>{ticket.enabledIntegrations.join(', ')}</Row>
				)}
				{account && (
					<Row label='Status'>
						<span className={css(s.detailAccountStatus, s[`detailAccountStatus_${accountStatus.state}`])}>
							{accountStatus.label}
							{accountStatus.cancellationDate && ` · ${formatDate(accountStatus.cancellationDate)}`}
						</span>
					</Row>
				)}
				{contactTimeZone && <Row label='Timezone'>{contactTimeZone}</Row>}
				{websiteProvider && (
					<Row label='Site built with'>
						<Pill variant='tag' color={websiteProvider.color} size='small' label={websiteProvider.label} />
					</Row>
				)}
				{accountLinks.length > 0 && (
					<Row label='Links'>
						<span className={css(s.detailLinks)}>
							{accountLinks.map((link, index) => (
								<React.Fragment key={link.label}>
									{index > 0 && <span className={css(s.detailLinkSeparator)}>·</span>}
									{link.href ? (
										<a href={link.href} target='_blank' rel='noopener noreferrer' className={css(s.detailLink)}>
											{link.label}
										</a>
									) : (
										<Button variant='link' styles={[s.detailInlineLink]} onClick={link.onClick}>
											{link.label}
										</Button>
									)}
								</React.Fragment>
							))}
						</span>
					</Row>
				)}
			</div>

			<div className={css(s.detailSectionTitle)}>Conversation</div>
			<TicketDetailsEditor
				ticket={ticket}
				categories={categories}
				teamMembers={teamMembers}
				agentTicketCounts={agentTicketCounts}
				onTicketLeftView={onTicketLeftView}
				shortcutField={shortcutField}
				onShortcutFieldChange={onShortcutFieldChange}
			/>
			<div className={css(s.detailRows, s.detailRowsStacked)}>
				<Row label='Created'>{formatDate(ticket.creationDate)}</Row>
				<Row label='Messages'>
					{messageCount ?? 0}
					{noteCount ? ` · ${noteCount} notes` : ''}
				</Row>
			</div>

			<PreviousConversations
				tickets={linkedTickets}
				onPreviewTicket={selectedId => setPreview({ ticketId: selectedId, fromConversationList: false })}
				onViewAll={() => setShowAllConversations(true)}
			/>

			<GitHubPanel
				ticketId={ticketId}
				hasPendingAiDraft={hasPendingAiDraft}
				onCreateIssue={() => setGitHubModal('create')}
				onLinkIssue={() => setGitHubModal('link')}
			/>

			{ticket.accountId && <TicketAdsPanel accountId={ticket.accountId} account={account} />}

			{gitHubModal === 'create' && (
				<CreateGitHubIssueModal ticketId={ticketId} ticket={ticket} onClose={() => setGitHubModal(null)} />
			)}
			{gitHubModal === 'link' && <LinkGitHubIssueModal ticketId={ticketId} onClose={() => setGitHubModal(null)} />}
			{showAllConversations && (
				<PreviousConversationsModal
					tickets={linkedTickets}
					contactLabel={contactLabel}
					agentNameMap={agentNameMap}
					onSelectTicket={selectedId => {
						setShowAllConversations(false);
						setPreview({ ticketId: selectedId, fromConversationList: true });
					}}
					page={conversationsPage}
					onPageChange={setConversationsPage}
					onClose={() => setShowAllConversations(false)}
				/>
			)}
			{preview && (
				<TicketPreviewModal
					sourceTicketId={preview.ticketId}
					targetTicketId={ticketId}
					onBack={
						preview.fromConversationList
							? () => {
									setPreview(null);
									setShowAllConversations(true);
								}
							: undefined
					}
					onClose={() => setPreview(null)}
					onMergeSuccess={() => setPreview(null)}
					onOpenTicket={id => onSelectTicket?.(id)}
				/>
			)}
			{account && (
				<ScheduleMeetingModal
					account={account}
					userSession={userSession}
					modalProps={{
						isOpen: showScheduleMeetingModal,
						onRequestClose: () => setShowScheduleMeetingModal(false),
					}}
				/>
			)}
		</>
	);
};
